"use client";

import { RiCalendar2Line, RiCheckLine } from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import { Spinner } from "@workspace/ui/components/spinner";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandItem,
} from "@workspace/ui/components/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@workspace/ui/components/popover";
import { Skeleton } from "@workspace/ui/components/skeleton";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useMemo, useState, Suspense } from "react";
import { useRelatedTourShows } from "@/hooks/features/show/data/use-related-tour-shows";
import { cn } from "@/lib/utils";

interface TourShow {
	id: string;
	title: string;
	slug: string;
	date: string;
	day: string | null;
	tour: string | null;
}

interface ShowTourSelectorProps {
	currentShow: {
		id: string;
		title: string;
		slug: string;
		tour?: string | null;
		day?: string | null;
	};
	className?: string;
}

interface ParsedTourShow {
	show: TourShow;
	tour: string;
	day: string | null;
	displayTour: string;
	displayDay: string;
}

// Parse tour shows into structured data
function parseTourShows(shows: TourShow[]): ParsedTourShow[] {
	return shows.map((show) => {
		const tour = show.tour || "";
		const day = show.day;

		return {
			show,
			tour,
			day,
			displayTour: tour,
			displayDay: day || "",
		};
	});
}

// Group shows by day
function groupByDay(versions: ParsedTourShow[]): Map<string, ParsedTourShow[]> {
	const grouped = new Map<string, ParsedTourShow[]>();

	for (const version of versions) {
		if (version.day) {
			const existing = grouped.get(version.day) || [];
			grouped.set(version.day, [...existing, version]);
		}
	}

	// Sort days within each group by date
	for (const [day, dayVersions] of grouped) {
		grouped.set(
			day,
			dayVersions.sort((a, b) => {
				// Parse dates without timezone conversion
				const partsA = a.show.date.split("-");
				const yearA = Number(partsA[0]);
				const monthA = Number(partsA[1]);
				const dayA = Number(partsA[2]);
				const partsB = b.show.date.split("-");
				const yearB = Number(partsB[0]);
				const monthB = Number(partsB[1]);
				const dayB = Number(partsB[2]);
				const dateA = new Date(yearA, monthA - 1, dayA);
				const dateB = new Date(yearB, monthB - 1, dayB);
				return dateA.getTime() - dateB.getTime();
			}),
		);
	}

	return grouped;
}

function ShowTourSelectorInner({
	currentShow,
	className,
}: ShowTourSelectorProps) {
	const router = useRouter();
	const searchParams = useSearchParams();
	const [dayOpen, setDayOpen] = useState(false);
	const [isNavigating, setIsNavigating] = useState(false);

	// Fetch related tour shows using TanStack Query (same pattern as festivals)
	const { data: relatedTourShows = [], isLoading } = useRelatedTourShows(
		currentShow.tour || null,
	);

	// Parse and organize tour shows
	const { currentVersion, availableDays, versionsByDay } = useMemo(() => {
		const versions = parseTourShows(relatedTourShows);
		const current = versions.find((v) => v.show.slug === currentShow.slug);
		const grouped = groupByDay(versions);

		// Sort days by actual chronological order (earliest date first)
		// Build a map of day name to earliest date for that day
		const dayToDate = new Map<string, Date>();
		for (const version of versions) {
			if (version.day) {
				const showDate = new Date(version.show.date);
				const existingDate = dayToDate.get(version.day);
				if (!existingDate || showDate < existingDate) {
					dayToDate.set(version.day, showDate);
				}
			}
		}

		// Sort days by their earliest date
		const days = Array.from(grouped.keys()).sort((a, b) => {
			const dateA = dayToDate.get(a);
			const dateB = dayToDate.get(b);
			if (!dateA || !dateB) return 0;
			return dateA.getTime() - dateB.getTime();
		});

		return {
			currentVersion: current,
			availableDays: days,
			versionsByDay: grouped,
		};
	}, [relatedTourShows, currentShow.slug]);

	// Handle navigation to a different show
	const handleShowChange = useCallback(
		async (targetSlug: string) => {
			// Preserve current URL parameters
			const currentParams = new URLSearchParams(searchParams?.toString() || "");

			// Show loading state
			setIsNavigating(true);

			// Build the new path
			const newPath = `/shows/${targetSlug}`;

			// Navigate to new show preserving query params
			const queryString = currentParams.toString();
			const fullPath = queryString ? `${newPath}?${queryString}` : newPath;

			router.push(fullPath);
		},
		[router, searchParams],
	);

	// Handle day change
	const handleDayChange = useCallback(
		(day: string) => {
			const versionsForDay = versionsByDay.get(day) || [];

			if (versionsForDay.length === 0) return;

			// Navigate to the first show for this day
			const targetVersion = versionsForDay[0];
			if (targetVersion) {
				handleShowChange(targetVersion.show.slug);
			}
		},
		[versionsByDay, handleShowChange],
	);

	// Don't show selector if there's only one show in the tour
	if (!isLoading && relatedTourShows.length <= 1) {
		return null;
	}

	// Loading state - use skeleton like festivals
	if (isLoading) {
		return (
			<div className={cn("flex items-center gap-2", className)}>
				<Skeleton className="h-8 w-20" />
				<Skeleton className="h-8 w-12" />
			</div>
		);
	}

	const currentTour = currentVersion?.displayTour || currentShow.tour || "";
	const currentDay = currentVersion?.displayDay || currentShow.day || "";
	const hasMultipleDays = availableDays.length > 1;

	return (
		<div className={cn("flex items-center gap-2", className)}>
			{/* Tour Selector - Display only (shows current tour name) */}
			<Button
				variant="secondary"
				size="sm"
				className={cn(
					"pointer-events-none inline-flex items-center text-sm",
					isNavigating && "opacity-50",
				)}
			>
				{isNavigating ? (
					<Spinner className="mr-1 h-4 w-4 animate-spin" />
				) : (
					<RiCalendar2Line className="mr-1 h-4 w-4" />
				)}
				<span>{currentTour}</span>
			</Button>

			{/* Day Selector - only show if multiple days exist */}
			{hasMultipleDays && (
				<Popover open={dayOpen} onOpenChange={setDayOpen}>
					<PopoverTrigger asChild>
						<Button
							variant="secondary"
							size="sm"
							className={cn(
								"text-sm",
								isNavigating && "pointer-events-none opacity-50",
							)}
						>
							{isNavigating ? (
								<Spinner className="h-4 w-4 animate-spin" />
							) : (
								currentDay
							)}
						</Button>
					</PopoverTrigger>
					<PopoverContent className="w-[200px] p-0" align="end">
						<Command>
							<CommandEmpty>No day found.</CommandEmpty>
							<CommandGroup>
								{availableDays.map((day) => (
									<CommandItem
										key={day}
										value={day}
										onSelect={() => {
											handleDayChange(day);
											setDayOpen(false);
										}}
									>
										{day}
										{day === currentDay && (
											<RiCheckLine className="ml-auto h-4 w-4" />
										)}
									</CommandItem>
								))}
							</CommandGroup>
						</Command>
					</PopoverContent>
				</Popover>
			)}
		</div>
	);
}

export function ShowTourSelector(props: ShowTourSelectorProps) {
	return (
		<Suspense fallback={<div className={cn("flex items-center gap-2", props.className)}><Skeleton className="h-8 w-20" /><Skeleton className="h-8 w-12" /></div>}>
			<ShowTourSelectorInner {...props} />
		</Suspense>
	);
}
